import { Box, HStack, Text } from '@chakra-ui/react'
import { useMemo } from 'react'

interface PasswordStrengthIndicatorProps {
  password: string
}

interface PasswordStrength {
  score: number // 0-4
  label: string
  color: string
}

export function PasswordStrengthIndicator({ password }: PasswordStrengthIndicatorProps) {
  const strength = useMemo((): PasswordStrength => {
    if (!password) {
      return { score: 0, label: '', color: 'gray.300' }
    }

    let score = 0
    const checks = {
      length: password.length >= 12,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      numbers: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
      veryLong: password.length >= 16,
    }

    // Base scoring
    if (checks.length) score++
    if (checks.uppercase && checks.lowercase) score++
    if (checks.numbers) score++
    if (checks.special) score++
    if (checks.veryLong) score++

    // Penalties
    if (password.length < 8) score = 0
    if (password.length < 10 && score > 1) score = 1

    // Cap at 4
    score = Math.min(4, score)

    const labels = ['Très faible', 'Faible', 'Moyen', 'Fort', 'Très fort']
    const colors = ['red.500', 'orange.500', 'yellow.500', 'green.500', 'green.600']

    return {
      score,
      label: labels[score] || '',
      color: colors[score] || 'gray.300',
    }
  }, [password])

  if (!password) return null

  return (
    <Box>
      {/* Strength bars */}
      <HStack gap={1} mb={2}>
        {[0, 1, 2, 3].map((index) => (
          <Box
            key={index}
            h="4px"
            flex={1}
            bg={index < strength.score ? strength.color : 'gray.300'}
            borderRadius="full"
            transition="all 0.3s"
          />
        ))}
      </HStack>

      {/* Label */}
      <Text fontSize="xs" color={strength.color} fontWeight="medium">
        {strength.label}
      </Text>

      {/* Requirements */}
      {strength.score < 3 && (
        <Box mt={2}>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Pour renforcer votre mot de passe :
          </Text>
          <Box fontSize="xs" color="gray.600">
            {password.length < 12 && <Text>• Au moins 12 caractères</Text>}
            {!/[A-Z]/.test(password) && <Text>• Au moins une majuscule</Text>}
            {!/[a-z]/.test(password) && <Text>• Au moins une minuscule</Text>}
            {!/[0-9]/.test(password) && <Text>• Au moins un chiffre</Text>}
            {!/[^A-Za-z0-9]/.test(password) && <Text>• Au moins un caractère spécial</Text>}
          </Box>
        </Box>
      )}
    </Box>
  )
}
